import { useEffect, useState } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { AlertCircle } from 'lucide-react'
import { classifyAuthError } from '@shared/api/httpErrors'
import { getProfile, useInvalidateProfile } from '@entities/user'
import { useAppStore } from '@/store/useAppStore'
import { usePlatform } from '@shared/platform'
import { ROUTES } from '@shared/config/routes'
import { confirmLinkTelegram, type LinkTelegramConfirmResponse } from '../api/linkTelegramApi'
import styles from './LinkTelegramConfirmScreen.module.css'

type Status = 'preview' | 'pending' | 'success' | 'error'

const ERROR_MESSAGES: Record<string, string> = {
  token_not_found: 'Ссылка недействительна.',
  token_expired: 'Ссылка истекла. Попросите сгенерировать новую.',
  token_already_used: 'Ссылка уже использована.',
  user_already_has_telegram: 'Этот аккаунт уже связан с другим Telegram.',
  unauthorized: 'Откройте это приложение в Telegram.'
}

/**
 * Экран подтверждения связки Telegram с web-аккаунтом (TG-сборка).
 * Сначала показывает preview с кнопками "Связать" / "Не тот аккаунт";
 * confirm вызывается только после явного подтверждения пользователем.
 */
export const LinkTelegramConfirmScreen = () => {
  const [params] = useSearchParams()
  const token = params.get('token') ?? ''
  const navigate = useNavigate()
  const invalidateProfile = useInvalidateProfile()
  const setProfile = useAppStore((s) => s.setProfile)
  const platform = usePlatform()
  const [status, setStatus] = useState<Status>('preview')
  const [result, setResult] = useState<LinkTelegramConfirmResponse | null>(null)
  const [errorText, setErrorText] = useState<string>('')
  const [wrongAccountOpen, setWrongAccountOpen] = useState(false)

  useEffect(() => {
    if (!token) {
      setStatus('error')
      setErrorText('Нет токена связки')
    }
  }, [token])

  const handleConfirm = async () => {
    if (!token) return
    setStatus('pending')
    try {
      const res = await confirmLinkTelegram(token)
      setResult(res)
      setStatus('success')
      await invalidateProfile()
    } catch (err) {
      const anyErr = err as { response?: { status?: number; data?: unknown } }
      // eslint-disable-next-line no-console
      console.warn('[link-telegram/confirm] error', anyErr?.response?.status, anyErr?.response?.data)
      // Если бот уже обработал ссылку (через свой handler), профиль уже содержит telegram.
      try {
        const profile = await getProfile()
        setProfile(profile)
        if (profile.telegram) {
          setResult({
            user_id: profile.user_id,
            merged: false,
            already_linked: true,
            email: profile.email?.email ?? null,
            merge_details: null
          })
          setStatus('success')
          await invalidateProfile()
          return
        }
      } catch { /* игнор — покажем ошибку ниже */ }
      const kind = classifyAuthError(err)
      setErrorText(ERROR_MESSAGES[kind] ?? 'Не удалось подтвердить связку.')
      setStatus('error')
    }
  }

  const goHome = () => navigate(ROUTES.HOME, { replace: true })
  const closeApp = () => platform.close()

  if (status === 'pending') {
    return <div className={styles.pending}>Подтверждаем связку…</div>
  }

  if (status === 'error') {
    return (
      <div className={styles.screen}>
        <div className={styles.header}>
          <AlertCircle className={styles.icon} strokeWidth={1.5} aria-hidden="true" />
          <div className={styles.headings}>
            <h1 className={styles.title}>Связка Telegram</h1>
            <p className={styles.error}>{errorText}</p>
          </div>
        </div>
        <div className={styles.actions}>
          <button type="button" onClick={goHome} className={styles.primary}>В приложение</button>
        </div>
      </div>
    )
  }

  if (status === 'success') {
    const r = result!
    let title = 'Ваш Telegram связан'
    let subtitle = r.email ? `Аккаунт: ${r.email}` : ''
    if (r.already_linked) {
      title = 'Уже связан'
      subtitle = r.email ? `Аккаунт: ${r.email}` : ''
    } else if (r.merged) {
      title = 'Аккаунты объединены'
      subtitle = r.email ? `Основной аккаунт: ${r.email}` : ''
    }
    return (
      <div className={styles.screen}>
        <div className={styles.header}>
          <AlertCircle className={styles.icon} strokeWidth={1.5} aria-hidden="true" />
          <div className={styles.headings}>
            <h1 className={styles.title}>{title}</h1>
            {subtitle && <p className={styles.subtitle}>{subtitle}</p>}
          </div>
        </div>
        <div className={styles.actions}>
          <button type="button" onClick={goHome} className={styles.primary}>В приложение</button>
        </div>
      </div>
    )
  }

  // status === 'preview'
  return (
    <>
      <div className={styles.screen}>
        <div className={styles.header}>
          <AlertCircle className={styles.icon} strokeWidth={1.5} aria-hidden="true" />
          <div className={styles.headings}>
            <h1 className={styles.title}>Связать Telegram с аккаунтом</h1>
            <p className={styles.text}>
              Вы собираетесь связать этот Telegram-аккаунт с вашим web-аккаунтом. После
              подтверждения вы сможете входить в приложение как через Telegram, так и через браузер.
            </p>
          </div>
        </div>
        <div className={styles.actions}>
          <button type="button" onClick={handleConfirm} className={styles.primary}>
            Связать
          </button>
          <button type="button" onClick={() => setWrongAccountOpen(true)} className={styles.secondary}>
            Не тот аккаунт
          </button>
        </div>
      </div>

      {wrongAccountOpen && (
        <div className={styles.modalOverlay} onClick={() => setWrongAccountOpen(false)}>
          <div className={styles.modalSheet} onClick={(e) => e.stopPropagation()}>
            <h2 className={styles.modalTitle}>Как сменить Telegram-аккаунт</h2>
            <ol className={styles.list}>
              <li>Закройте это окно.</li>
              <li>Переключитесь на нужный Telegram-аккаунт в приложении.</li>
              <li>Снова нажмите «Привязать Telegram» в веб-версии — откроется новая ссылка в выбранном аккаунте.</li>
              <li>Подтвердите привязку.</li>
            </ol>
            <div className={styles.actions}>
              <button type="button" onClick={closeApp} className={styles.primary}>
                Закрыть
              </button>
              <button type="button" onClick={() => setWrongAccountOpen(false)} className={styles.secondary}>
                Назад
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  )
}
